import {
  Stack,
  Text,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Button,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import MvrRequestResultRow from './MvrRequestResultRow';
import type { MVRRequestStatusResponse } from 'api';

interface MvrRequestStatusProps {
  mvrOrderId: number;
  status: MVRRequestStatusResponse | undefined;
  isLoading: boolean;
  onReset: () => void;
  backLabel?: string;
}

const MvrRequestStatus = ({
  mvrOrderId,
  status,
  isLoading,
  onReset,
  backLabel = 'Back',
}: MvrRequestStatusProps) => {
  const textColor = useColorModeValue('gray.700', 'white');

  if (isLoading && !status) {
    return (
      <Flex justify="center" py={10}>
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (!status || status.status === 'in_progress') {
    return (
      <Stack spacing={4} align="center" py={10}>
        <Spinner size="lg" color="brand.500" />
        <Text color={textColor}>Order #{mvrOrderId} is being processed…</Text>
        <Text fontSize="sm" color="gray.500">
          This can take a few seconds, or up to 24 hours for some provinces. You
          can leave this page — the request will keep processing.
        </Text>
        <Button variant="outline" onClick={onReset}>
          {backLabel}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={4}>
      {(status.status === 'error' ||
        status.status === 'service_unavailable') && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{status.error}</AlertDescription>
        </Alert>
      )}

      <Text fontSize="sm" fontWeight="700" color={textColor}>
        Order #{mvrOrderId} results
      </Text>

      <Stack divider={<Divider />} spacing={0}>
        {status.drivers.map((driver) => (
          <MvrRequestResultRow
            key={driver.license_number}
            driver={driver}
            mvrOrderId={mvrOrderId}
          />
        ))}
      </Stack>

      <Flex justify="flex-end">
        <Button colorScheme="brand" onClick={onReset}>
          {backLabel}
        </Button>
      </Flex>
    </Stack>
  );
};

export default MvrRequestStatus;
